const DB_NAME = 'aibuild-app-links';
const STORE = 'pendingStarts';
const TTL_MS = 60 * 60 * 1000;

interface Entry<T> {
  readonly appKey: string;
  readonly request: T;
  readonly savedAt: number;
}

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const opening = indexedDB.open(DB_NAME, 1);
    opening.onupgradeneeded = () => opening.result.createObjectStore(STORE, { keyPath: 'appKey' });
    opening.onsuccess = () => resolve(opening.result);
    opening.onerror = () => reject(opening.error ?? new Error('IndexedDB is unavailable'));
  });

const inStore = async <T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = work(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
  } finally {
    db.close();
  }
};

const isFresh = (entry: Entry<unknown>) => Date.now() - entry.savedAt < TTL_MS;

// The entries hold a visitor's file bytes, so expired ones are deleted, not just ignored.
const deleteExpired = (store: IDBObjectStore) => {
  const cursor = store.openCursor();
  cursor.onsuccess = () => {
    const current = cursor.result;
    if (!current) return;
    if (!isFresh(current.value as Entry<unknown>)) current.delete();
    current.continue();
  };
};

/**
 * The Run a signed-out visitor asked for on an app's public link, kept so they
 * can pick it up after signing up. IndexedDB because it holds the picked files'
 * bytes and outlives the tab the verification email opens.
 */
export const pendingAppStarts = {
  save: async <T>(appKey: string, request: T): Promise<void> => {
    await inStore('readwrite', (store) => {
      deleteExpired(store);
      return store.put({ appKey, request, savedAt: Date.now() } satisfies Entry<T>);
    });
  },
  peek: async <T>(appKey: string): Promise<T | null> => {
    const entry = (await inStore('readwrite', (store) => {
      deleteExpired(store);
      return store.get(appKey);
    })) as Entry<T> | undefined;
    return entry && isFresh(entry) ? entry.request : null;
  },
  remove: async (appKey: string): Promise<void> => {
    await inStore('readwrite', (store) => store.delete(appKey));
  },
  clearAll: async (): Promise<void> => {
    await inStore('readwrite', (store) => store.clear());
  },
};
